import React from 'react';
import PropTypes from 'prop-types';
import { TableBody, TableCell, TableRow, IconButton, Tooltip, Box } from '@mui/material';
import { Archive, Edit, ContentCopy, CheckCircle, Cancel, PlayArrow, Send, Visibility } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { ROUTES } from '../../../../constants/routes';
import { getRouteWithParam } from '../../../../constants/routes';
import { StatusColor } from '../../../../components/StatusColor';
import { formatDate } from '../../../../utils/formatDate';

export const CompanyPromotionsTableBody = ({ 
  data, 
  onArchive, 
  onApprove,
  onReject,
  onActivate,
  onSendForApproval,
  loading = false 
}) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'NEW':
        return 'info';
      case 'IN_APPROVAL':
        return 'warning';
      case 'REJECTED':
        return 'error';
      case 'APPROVED':
        return 'success';
      case 'ACTIVE':
        return 'success';
      case 'DEACTIVATED':
        return 'secondary';
      case 'WAITING_FOR_ACTIVATION':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'NEW':
        return 'Новая';
      case 'IN_APPROVAL':
        return 'На рассмотрении';
      case 'REJECTED':
        return 'Отклонена';
      case 'APPROVED':
        return 'Одобрена';
      case 'ACTIVE':
        return 'Активна';
      case 'DEACTIVATED':
        return 'Деактивирована';
      case 'WAITING_FOR_ACTIVATION':
        return 'Ожидание активации';
      default:
        return status;
    }
  };

  const canEdit = (status) => {
    // Можно редактировать только новые акции
    return status === 'NEW' || status === 'REJECTED' || status === 'ACTIVE';
  };


  const canArchive = (status) => {
    // Можно архивировать только активные акции
    return status === 'ACTIVE';
  };

  const canCopy = (status) => {
    // Можно копировать деактивированные акции
    return status === 'DEACTIVATED';
  };

  const canApprove = (status) => {
    // Можно одобрить акции на рассмотрении
    return status === 'IN_APPROVAL';
  };

  const canReject = (status) => {
    // Можно отклонить акции на рассмотрении
    return status === 'IN_APPROVAL';
  };

  const canActivate = (status) => {
    // Можно активировать одобренные акции
    return status === 'APPROVED';
  };

  const canSendForApproval = (status) => {
    // Можно отправить на рассмотрение только новые акции
    return status === 'NEW' || status === 'REJECTED';
  };

  const canView = () => {
    // Можно просматривать все акции
    return true;
  };


  return (
    <TableBody>
      {data?.map((row, index) => (
        <TableRow key={row.id} hover>
          <TableCell>{index + 1}</TableCell>
          <TableCell>{row.id}</TableCell>
          <TableCell>
            <Box sx={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {row.translations?.[0]?.promotion_name || 'Без названия'}
            </Box>
          </TableCell>
          <TableCell>
            <Box sx={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {row.translations?.[0]?.short_description || 'Без описания'}
            </Box>
          </TableCell>
          <TableCell>
            {formatDate(row.start_at)}
          </TableCell>
          <TableCell>
            {formatDate(row.end_at)}
          </TableCell>
          <TableCell>
            <StatusColor 
              color={getStatusColor(row.status)} 
              text={getStatusText(row.status)} 
            />
          </TableCell>
          <TableCell>
            <Box display="flex" gap="4px">
              {canView(row.status) && (
                <Tooltip title="Просмотр">
                  <IconButton
                    component={Link}
                    to={getRouteWithParam(ROUTES.COMPANY_PROMOTIONS_DETAIL, ':id', row.id)}
                    size="small"
                    disabled={loading}>
                    <Visibility fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {canEdit(row.status) && (
                <Tooltip title="Редактировать">
                  <IconButton
                    component={Link}
                    to={getRouteWithParam(ROUTES.COMPANY_PROMOTIONS_EDIT, ':id', row.id)}
                    size="small"
                    disabled={loading}>
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {canSendForApproval(row.status) && (
                <Tooltip title="Отправить на рассмотрение">
                  <IconButton
                    onClick={() => onSendForApproval && onSendForApproval(row)}
                    size="small"
                    disabled={loading}>
                    <Send fontSize="small" color="primary" />
                  </IconButton>
                </Tooltip>
              )}

              {canApprove(row.status) && (
                <Tooltip title="Одобрить">
                  <IconButton
                    onClick={() => onApprove && onApprove(row)}
                    size="small"
                    disabled={loading}>
                    <CheckCircle fontSize="small" color="success" />
                  </IconButton>
                </Tooltip>
              )}

              {canReject(row.status) && (
                <Tooltip title="Отклонить">
                  <IconButton
                    onClick={() => onReject && onReject(row)}
                    size="small"
                    disabled={loading}>
                    <Cancel fontSize="small" color="error" />
                  </IconButton>
                </Tooltip>
              )}

              {canActivate(row.status) && (
                <Tooltip title="Активировать">
                  <IconButton
                    onClick={() => onActivate && onActivate(row)}
                    size="small"
                    disabled={loading}>
                    <PlayArrow fontSize="small" color="primary" />
                  </IconButton>
                </Tooltip>
              )}

              {canArchive(row.status) && (
                <Tooltip title="Деактивировать">
                  <IconButton
                    onClick={() => onArchive(row)}
                    size="small"
                    disabled={loading}>
                    <Archive fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {canCopy(row.status) && (
                <Tooltip title="Копировать">
                  <IconButton
                    component={Link}
                    to={getRouteWithParam(ROUTES.COMPANY_PROMOTIONS_COPY, ':id', row.id)}
                    size="small"
                    disabled={loading}>
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

            </Box>
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  );
};

CompanyPromotionsTableBody.propTypes = {
  data: PropTypes.array,
  onArchive: PropTypes.func,
  onApprove: PropTypes.func,
  onReject: PropTypes.func,
  onActivate: PropTypes.func,
  onSendForApproval: PropTypes.func,
  loading: PropTypes.bool
};
